// src/components/BrokerCRM/steps/ApplicantStep.js
import React, { useState, useEffect } from "react";

function ApplicantStep({ crmData = {}, updateCrmData, onNext }) {
  // Default blank applicant template
  const createNewApplicant = (id) => ({
    id,
    title: "",
    firstName: "",
    middleName: "",
    lastName: "",
    dobDay: "",
    dobMonth: "",
    dobYear: "",
    dob: "",
    mobile: "",
    email: "",
    relationship: "",
    residency: "",
    creditHistory: "",
  });

  // Ensure applicants array always exists
  const initialApplicants =
    crmData.applicants && crmData.applicants.length > 0
      ? crmData.applicants
      : [createNewApplicant(1)];

  const [localApplicants, setLocalApplicants] = useState(initialApplicants);
  const [activeApplicant, setActiveApplicant] = useState(0);

  // 🔄 Always sync with crmData automatically
  useEffect(() => {
    updateCrmData("applicants", localApplicants);
  }, [localApplicants, updateCrmData]);

  const handleChange = (index, field, value) => {
    const updated = [...localApplicants];
    updated[index] = { ...updated[index], [field]: value };

    // Auto-combine DOB if all 3 fields present
    if (
      ["dobDay", "dobMonth", "dobYear"].includes(field) &&
      updated[index].dobDay &&
      updated[index].dobMonth &&
      updated[index].dobYear
    ) {
      updated[index].dob = `${updated[index].dobYear}-${updated[
        index
      ].dobMonth.padStart(2, "0")}-${updated[index].dobDay.padStart(2, "0")}`;
    }

    setLocalApplicants(updated);
  };

  const addApplicant = () => {
    if (localApplicants.length < 2) {
      const newApplicant = createNewApplicant(localApplicants.length + 1);
      setLocalApplicants([...localApplicants, newApplicant]);
      setActiveApplicant(localApplicants.length); // switch to the new one
    }
  };

  // Progress calculation (simple % of filled fields)
  const calculateProgress = (applicant) => {
    const fields = [
      "title",
      "firstName",
      "lastName",
      "dob",
      "mobile",
      "email",
      "relationship",
      "residency",
      "creditHistory",
    ];
    const filled = fields.filter((f) => applicant[f] && applicant[f] !== "")
      .length;
    return Math.round((filled / fields.length) * 100);
  };

  return (
    <div>
      <h2>Loan Applicants</h2>

      {/* Applicant Cards */}
      <div style={{ display: "flex", gap: "1rem", marginBottom: "1rem" }}>
        {localApplicants.map((applicant, index) => {
          const progress = calculateProgress(applicant);
          return (
            <div
              key={index}
              onClick={() => setActiveApplicant(index)}
              style={{
                flex: 1,
                padding: "1rem",
                border:
                  activeApplicant === index
                    ? "2px solid #4EDCF6"
                    : "1px solid #ccc",
                borderRadius: "8px",
                cursor: "pointer",
                background: activeApplicant === index ? "#e8faff" : "white",
              }}
            >
              <h4>Applicant {index + 1}</h4>
              <p>
                {applicant.firstName || "First name"} {applicant.lastName || ""}
              </p>
              <p>{applicant.email || "No email added"}</p>

              {/* Progress Bar */}
              <div style={{ marginTop: "0.5rem" }}>
                <div
                  style={{
                    height: "6px",
                    background: "#ddd",
                    borderRadius: "4px",
                    overflow: "hidden",
                  }}
                >
                  <div
                    style={{
                      width: `${progress}%`,
                      background: "#4EDCF6",
                      height: "100%",
                    }}
                  ></div>
                </div>
                <small>{progress}% Complete</small>
              </div>
            </div>
          );
        })}

        {/* Add Applicant Button */}
        {localApplicants.length < 2 && (
          <button
            onClick={addApplicant}
            style={{
              padding: "1rem",
              border: "1px dashed #aaa",
              borderRadius: "8px",
              cursor: "pointer",
              background: "#fafafa",
            }}
          >
            + Add Applicant
          </button>
        )}
      </div>

      {/* Active Applicant Form */}
      {localApplicants[activeApplicant] && (
        <div
          style={{ background: "#fff", padding: "1.5rem", borderRadius: "8px" }}
        >
          <h3>Applicant {activeApplicant + 1} Details</h3>

          <div style={{ display: "flex", gap: "2rem" }}>
            {/* Contact Details */}
            <div style={{ flex: 1 }}>
              <h4>Contact details</h4>
              <div style={{ display: "grid", gap: "1rem" }}>
                <div>
                  <label>Title</label>
                  <select
                    value={localApplicants[activeApplicant].title}
                    onChange={(e) =>
                      handleChange(activeApplicant, "title", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    <option value="Mr">Mr</option>
                    <option value="Mrs">Mrs</option>
                    <option value="Ms">Ms</option>
                    <option value="Miss">Miss</option>
                    <option value="Dr">Dr</option>
                  </select>
                </div>
                <div>
                  <label>First Name</label>
                  <input
                    type="text"
                    value={localApplicants[activeApplicant].firstName}
                    onChange={(e) =>
                      handleChange(activeApplicant, "firstName", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label>Middle Name</label>
                  <input
                    type="text"
                    value={localApplicants[activeApplicant].middleName}
                    onChange={(e) =>
                      handleChange(
                        activeApplicant,
                        "middleName",
                        e.target.value
                      )
                    }
                  />
                </div>
                <div>
                  <label>Last Name</label>
                  <input
                    type="text"
                    value={localApplicants[activeApplicant].lastName}
                    onChange={(e) =>
                      handleChange(activeApplicant, "lastName", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label>Mobile</label>
                  <input
                    type="text"
                    value={localApplicants[activeApplicant].mobile}
                    onChange={(e) =>
                      handleChange(activeApplicant, "mobile", e.target.value)
                    }
                  />
                </div>
                <div>
                  <label>Email</label>
                  <input
                    type="email"
                    value={localApplicants[activeApplicant].email}
                    onChange={(e) =>
                      handleChange(activeApplicant, "email", e.target.value)
                    }
                  />
                </div>
              </div>
            </div>

            {/* Personal Details */}
            <div style={{ flex: 1 }}>
              <h4>Personal details</h4>
              <div style={{ display: "grid", gap: "1rem" }}>
                <div>
                  <label>Date of Birth</label>
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <input
                      type="text"
                      placeholder="DD"
                      maxLength={2}
                      style={{ width: "60px" }}
                      value={localApplicants[activeApplicant].dobDay}
                      onChange={(e) =>
                        handleChange(activeApplicant, "dobDay", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      placeholder="MM"
                      maxLength={2}
                      style={{ width: "60px" }}
                      value={localApplicants[activeApplicant].dobMonth}
                      onChange={(e) =>
                        handleChange(activeApplicant, "dobMonth", e.target.value)
                      }
                    />
                    <input
                      type="text"
                      placeholder="YYYY"
                      maxLength={4}
                      style={{ width: "80px" }}
                      value={localApplicants[activeApplicant].dobYear}
                      onChange={(e) =>
                        handleChange(activeApplicant, "dobYear", e.target.value)
                      }
                    />
                  </div>
                </div>

                <div>
                  <label>Relationship Status</label>
                  <select
                    value={localApplicants[activeApplicant].relationship}
                    onChange={(e) =>
                      handleChange(
                        activeApplicant,
                        "relationship",
                        e.target.value
                      )
                    }
                  >
                    <option value="">Select</option>
                    <option value="Single">Single</option>
                    <option value="Married to Applicant 1">
                      Married to Applicant 1
                    </option>
                    <option value="Married to Applicant 2">
                      Married to Applicant 2
                    </option>
                    <option value="Defacto">Defacto</option>
                    <option value="Separated">Separated</option>
                  </select>
                </div>

                <div>
                  <label>Residency Status</label>
                  <select
                    value={localApplicants[activeApplicant].residency}
                    onChange={(e) =>
                      handleChange(activeApplicant, "residency", e.target.value)
                    }
                  >
                    <option value="">Select</option>
                    <option value="Citizen">Australian Citizen</option>
                    <option value="PR">Permanent Resident</option>
                    <option value="Other">Other</option>
                  </select>
                </div>

                <div>
                  <label>Credit History</label>
                  <select
                    value={localApplicants[activeApplicant].creditHistory}
                    onChange={(e) =>
                      handleChange(
                        activeApplicant,
                        "creditHistory",
                        e.target.value
                      )
                    }
                  >
                    <option value="">Select</option>
                    <option value="Good">Good</option>
                    <option value="Average">Average</option>
                    <option value="Poor">Poor</option>
                  </select>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default ApplicantStep;
